import React from 'react';
import { ArrowUpRight, Plus } from 'lucide-react';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from './ui/accordion';
import { faqs, contactInfo } from '../mock';
import useReveal from '../hooks/useReveal';

const FAQ = () => {
  const ref = useReveal({ threshold: 0.08 });

  return (
    <section id="faq" ref={ref} className="relative py-16 md:py-20 px-6 md:px-10 border-t border-white/5 overflow-hidden">
      {/* Compass sculpture background — direction and clarity */}
      <div aria-hidden="true" className="pointer-events-none absolute inset-0">
        <img
          src="/faq-editorial.webp" srcSet="/faq-editorial-400w.webp 400w, /faq-editorial-800w.webp 800w, /faq-editorial.webp 2000w" sizes="(max-width: 640px) 400px, (max-width: 1024px) 800px, 2000px" loading="lazy" decoding="async"
          alt=""
          className="w-full h-full object-cover object-center opacity-[0.45]"
        />
        <div className="absolute inset-0 bg-[color:var(--obsidian)]/55" />
        <div className="absolute inset-0 bg-gradient-to-b from-[color:var(--obsidian)] via-transparent to-[color:var(--obsidian)]" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,transparent_0%,rgba(10,10,10,0.4)_70%,rgba(10,10,10,0.9)_100%)]" />
      </div>

      <div className="pointer-events-none absolute -left-24 top-40 w-[420px] h-[420px] rounded-full bg-[color:var(--parchment)]/[0.04] blur-3xl" />

      <div className="relative max-w-[1400px] mx-auto grid lg:grid-cols-[.45fr_.55fr] gap-14">
        <div>
          <div className="reveal font-mono text-[12px] md:text-[10.5px] tracking-[0.24em] uppercase text-[color:var(--green)] mb-4">
            &mdash; Common Questions
          </div>
          <h2 className="reveal font-display text-[38px] md:text-[62px] leading-[1.02] text-[color:var(--parchment)]">
            Straight <span className="text-[color:var(--green)]">answers</span>, no pressure.
          </h2>
          <p className="reveal mt-6 text-[color:var(--parchment)]/70 leading-relaxed max-w-md">
            A few of the questions we hear most often. If yours isn&rsquo;t here, a specialist is a call away &mdash; happy to walk you through anything specific to your estate.
          </p>

          <div className="reveal mt-10 p-5 border border-white/10 rounded-xl">
            <div className="font-mono text-[12px] md:text-[10.5px] tracking-[0.22em] uppercase text-[color:var(--parchment)]/50">
              Still have questions?
            </div>
            <a
              href={contactInfo.phoneHref}
              className="mt-2 block font-display text-2xl text-[color:var(--parchment)] hover:text-[color:var(--green)] transition-colors"
            >
              {contactInfo.phone}
            </a>
            <a
              href="#sell"
              className="mt-4 inline-flex items-center gap-2 text-[11.5px] font-semibold uppercase tracking-[0.14em] text-[color:var(--parchment)] hover:text-[color:var(--green)] transition-colors"
            >
              Or request a callback <ArrowUpRight className="w-3.5 h-3.5" />
            </a>
          </div>
        </div>

        <div className="reveal">
          <Accordion type="single" collapsible className="border-t border-white/10">
            {faqs.map((f, i) => (
              <AccordionItem
                key={f.q}
                value={`item-${i}`}
                className="border-b border-white/10 group"
              >
                <AccordionTrigger className="py-6 md:py-7 text-left hover:no-underline [&>svg]:hidden [&[data-state=open]_.faq-plus]:rotate-45 [&[data-state=open]_.faq-num]:text-[color:var(--green)]">
                  <div className="flex items-start gap-5 w-full">
                    <span className="faq-num shrink-0 mt-1 font-mono text-[12px] md:text-[10.5px] tracking-[0.22em] uppercase text-[color:var(--parchment)]/50 transition-colors">
                      {String(i + 1).padStart(2, '0')}
                    </span>
                    <span className="flex-1 font-display text-[18px] md:text-[22px] leading-tight text-[color:var(--parchment)] pr-4">
                      {f.q}
                    </span>
                    <span className="shrink-0 mt-1">
                      <Plus className="faq-plus w-5 h-5 text-[color:var(--parchment)]/70 transition-transform duration-500 ease-[cubic-bezier(.2,.7,.2,1)]" />
                    </span>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="pb-7 pl-14 pr-10 text-[color:var(--parchment)]/70 text-[15px] leading-relaxed whitespace-pre-line">
                  {f.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </div>
    </section>
  );
};

export default FAQ;
